import type { z } from '@repo/schema'
import type { GetCompanySearchResultSchema } from '@repo/schema/company'

import type { CompanyResult, CompanyStatistics } from './company-result-row'

export type CompanySearchResult = z.infer<typeof GetCompanySearchResultSchema>

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })

type CompanyAddressSource = null | {
  address?: null | string
  address_second_line?: null | string
  city?: null | string
  postcode?: null | string
}

type CompanyStatisticsSource = Pick<
  CompanySearchResult['statistics'],
  'carrier_jobs_count' | 'contacts_count' | 'customer_loads_count' | 'locations_count'
>

/** The street on one line, then the postcode and city, as company search shows them. */
export const toCompanyAddressLines = (address: CompanyAddressSource | undefined) => {
  const street = [address?.address, address?.address_second_line].filter(Boolean).join(', ')
  const town = [address?.postcode, address?.city].filter(Boolean).join(' ')

  return [street, town].filter(Boolean)
}

export const toCompanyStatistics = (statistics: CompanyStatisticsSource): CompanyStatistics => ({
  contacts: statistics.contacts_count,
  jobs: statistics.carrier_jobs_count,
  loads: statistics.customer_loads_count,
  locations: statistics.locations_count,
})

/**
 * Shapes a `GET /companies/search` result for `CompanyResultRow`. Pickers that rank customers
 * pass `includeRisk`, so a missing risk profile shows as not verified.
 */
export const toCompanyResult = (
  result: CompanySearchResult,
  { includeRisk = false }: { includeRisk?: boolean } = {},
): CompanyResult => {
  let riskProfile: CompanyResult['riskProfile'] = undefined

  if (includeRisk) {
    riskProfile = result.risk_profile
      ? { score: result.risk_profile.score, validTo: result.risk_profile.valid_to }
      : null
  }

  return {
    addressLines: toCompanyAddressLines(result.address),
    country: result.country ? { code: result.country, name: regionNames.of(result.country) } : null,
    isBroker: result.type === 'broker',
    isMarketplace: result.is_marketplace,
    isXeroSynced: result.xero_synced,
    organisationSlug: result.organisation?.slug,
    riskProfile,
    statistics: toCompanyStatistics(result.statistics),
    title: result.title,
    vat: result.vat,
  }
}
